import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Video, Users, Clock, Shield, Mic, Camera, ScreenShare, MessageSquare, Play, ArrowRight } from "lucide-react";

const ZoomStudyRoom = () => {
  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "HD Video",
      description: "Crystal-clear video quality to see your study partners and stay engaged",
    },
    {
      icon: <ScreenShare className="w-8 h-8" />,
      title: "Screen Share",
      description: "Share your screen to collaborate on problems or review notes",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Live Chat",
      description: "Text chat for quick questions without interrupting others",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multiple Rooms",
      description: "Choose from various room sizes and themes to match your style",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Zoom Study Room - Video Study Sessions"
        description="Free video study rooms with HD webcam sharing, screen share, and live chat. Study with partners in real-time like Zoom, but built for studying."
        keywords="zoom study room, video study room, study with webcam, online study session, study video call, virtual study room"
        canonicalUrl="/zoom-study-room/"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-5xl mx-auto text-center space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Video className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">VIDEO STUDY ROOMS</span>
              </div>
              
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="text-gradient">Zoom Study Rooms</span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Connect face-to-face with study partners worldwide. Video-enabled study sessions that bring the library experience to your screen.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Join a Zoom Room <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
              
              {/* Video Section */}
              <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full aspect-video object-cover"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                      <Play className="w-5 h-5 text-primary-foreground ml-1" />
                    </div>
                    <span className="text-foreground font-medium">See how it works</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                  Powerful <span className="text-gradient">Features</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Everything you need for productive video study sessions
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <CardHeader>
                      <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {feature.icon}
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Video Study Rooms */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                Why Video <span className="text-gradient">Study Rooms?</span>
              </h2>
              <div className="space-y-8">
                {[
                  { icon: <Camera className="w-6 h-6" />, title: "Visual Accountability", desc: "Seeing others working motivates you to stay focused. The presence of video creates a shared commitment to productivity that's harder to break." },
                  { icon: <Mic className="w-6 h-6" />, title: "Real-Time Collaboration", desc: "Discuss complex topics, brainstorm solutions, and get immediate feedback from peers who are working on similar material." },
                  { icon: <Shield className="w-6 h-6" />, title: "Safe & Moderated", desc: "All rooms are moderated to maintain a respectful, productive environment. Report features ensure everyone can study comfortably." },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="flex gap-6 items-start">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary">
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold mb-2 text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground text-lg">{item.desc}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                How It <span className="text-gradient">Works</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { step: "1", title: "Choose a Room", desc: "Browse available Zoom study rooms by size, subject, or vibe that matches your needs." },
                  { step: "2", title: "Set Preferences", desc: "Choose whether to enable your camera, microphone, and what you want to share with others." },
                  { step: "3", title: "Start Studying", desc: "Join the room and get to work alongside motivated students from around the world." },
                ].map((item, index) => (
                  <div key={index} className="text-center space-y-4 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-primary">
                      <span className="text-3xl font-bold text-primary-foreground">{item.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Room Types */}
        <section className="py-24 relative overflow-hidden">
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-center mb-16">
                Room <span className="text-gradient">Types</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  { icon: <Clock className="w-8 h-8" />, title: "Pomodoro Rooms", desc: "Synchronized 25-minute focus sessions with 5-minute breaks. Perfect for structured productivity." },
                  { icon: <Users className="w-8 h-8" />, title: "Silent Study", desc: "Mics off, cameras on. Study in peaceful silence while staying accountable through visual presence." },
                  { icon: <MessageSquare className="w-8 h-8" />, title: "Collaborative", desc: "Open discussion rooms for group projects, study groups, and active learning with peers." },
                ].map((item, index) => (
                  <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                    <div className="text-primary mb-4">{item.icon}</div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Ready to Study <span className="text-gradient">Together?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of students who are transforming their study habits with video-enabled accountability.
              </p>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Enter a Zoom Room Now
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ZoomStudyRoom;